/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FlexRow,
    FlexCol,
    NotificationsWrapper,
    Icon,
    Button,
    Heading,
    ContentBlock,
} from 'sarsaparilla';
import PassNotification from './PassNotification';

const goTo = (id) => {
    document.getElementById(id).scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const items = [
    {
        id: 1,
        title: 'Access to only a single federal recreation site',
        value: 1,
    },
    {
        id: 2,
        title: 'Access to over 2,000 federal recreation sites',
        value: 2,
    },
    {
        id: 3,
        title: 'Price varies based on location and pass type (daily, weekly, seasonal, etc.)',
        value: 1,
    },
    {
        id: 4,
        title: 'Digital pass is available through Recreation.gov for instant download and use on a phone or tablet',
        value: 1,
    },
    {
        id: 5,
        title: 'Physical passes are available in person at federal recreational sites, or by mail when ordering through the USGS Online Store',
        value: 2,
    },
    {
        id: 6,
        title: 'Certain passes may provide discounts on some expanded amenity fees such as camping',
        value: 2,
    },
    {
        id: 7,
        title: 'Non-refundable, non-transferable, and cannot be replaced if lost or stolen',
        value: 3,
    },
    {
        id: 8,
        title: 'Proceeds from pass sales are used to improve and enhance visitor services',
        value: 3,
    },
];

const itemsOld = [
    {
        id: 1,
        title: 'Access to only a single federal recreation site',
        value: 1,
    },
    {
        id: 2,
        title: 'Access to over 2,000 federal recreation sites',
        value: 2,
    },
    {
        id: 3,
        title: 'Price varies based on location and pass type (daily, weekly, seasonal, etc.)',
        value: 1,
    },
    {
        id: 4,
        title: 'Price is $80 and valid for one full year (through month of purchase)',
        value: 2,
    },
    {
        id: 5,
        title: 'Digital pass is available for instant download and use on a phone or tablet',
        value: 1,
    },
    {
        id: 6,
        title: 'Physical pass is shipped in the mail and carried for use',
        value: 2,
    },
    {
        id: 7,
        title: 'Non-refundable, non-transferable, and cannot be replaced if lost or stolen',
        value: 3,
    },
    {
        id: 8,
        title: 'Proceeds from pass sales are used to improve and enhance visitor services',
        value: 3,
    },
];

const passTypes = [
    { id: 1, type: 'SITE PASSES', values: [1, 3] },
    { id: 2, type: 'INTERAGENCY PASSES', values: [2, 3] },
];

const defaultProps = {
    govtShutdown: false,
};
const propTypes = {
    govtShutdown: PropTypes.bool,
};

export default class PassComparisonSection extends React.PureComponent {
    render() {
        const list = this.props.interagencyStopExternalSales ? items : itemsOld;
        return (
            <ContentBlock className="pass-right-for-me-wrapper mt-6">
                <FlexRow>
                    <FlexCol className="pass-right-for-me-title-container">
                        <Heading
                            headingLevel={2}
                            appearance="h4"
                            hasUnderline="left"
                            className="rec-section-heading-pass mb-2"
                        >
                            How Passes Work
                        </Heading>

                        {/* Display an alert that is controlled by a LD flag */}
                        <PassNotification />
                        <div className="pass-right-for-me-text">
                            {!this.props.interagencyStopExternalSales && (
                                <p>
                                    Whether you’re planning a single trip or coming back
                                    multiple times a year, you can find the right pass for
                                    your visit on {process.env.SITE_NAME}. We currently
                                    offer Site Passes for use at national parks, forests,
                                    and other Federal recreational sites. Interagency
                                    Passes are available in-person or by mail from the
                                    USGS online store. You can learn more about these pass
                                    options below:
                                </p>
                            )}
                        </div>
                        {this.props.govtShutdown && (
                            <FlexRow>
                                <FlexCol smOffset={1} sm={10} mdOffset={2} md={8}>
                                    <NotificationsWrapper
                                        noticeType="warning"
                                        closeButton={false}
                                    >
                                        <div className="rec-notification-title">
                                            Government Shutdown:
                                        </div>
                                        <p>
                                            Interagency Pass and Your Pass Now sales are
                                            not available during the Government shutdown
                                            beginning on Saturday, December 22, 2018. We
                                            will resume pass sales after the shutdown has
                                            concluded.
                                        </p>
                                    </NotificationsWrapper>
                                    <p>&nbsp;</p>
                                </FlexCol>
                            </FlexRow>
                        )}
                    </FlexCol>
                </FlexRow>
                <table className="pass-comparison-big-table">
                    <caption className="rec-sr-only">Pass features</caption>
                    <thead>
                        <tr>
                            <th>FEATURES</th>
                            {passTypes.map((item) => (
                                <th
                                    className="pass-comparison-big-table-title"
                                    key={item.id}
                                >
                                    {item.type}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id}>
                                <td>{item.title}</td>
                                <td className="rec-row-pass-type">
                                    {(item.value === 1 || item.value === 3) && (
                                        <Icon
                                            iconName="check-circle"
                                            className="rec-icon-success"
                                            size="lg"
                                            aria-label="Validated"
                                        />
                                    )}
                                </td>
                                <td className="rec-row-pass-type">
                                    {(item.value === 2 || item.value === 3) && (
                                        <Icon
                                            iconName="check-circle"
                                            className="rec-icon-success"
                                            size="lg"
                                            aria-label="Validated"
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr className="pass-comparison-big-table-learn-more-empty">
                            <td />
                            <td className="pass-comparison-big-table-learn-more">
                                <Button
                                    appearance="link"
                                    onClick={() => goTo('site-specific-digital-passes')}
                                    aria-label="Learn more about site passes"
                                >
                                    Learn More
                                </Button>
                            </td>
                            <td className="pass-comparison-big-table-learn-more">
                                <Button
                                    appearance="link"
                                    onClick={() => goTo('interagency-annual-pass')}
                                    aria-label="Learn more about interagency passes"
                                >
                                    Learn More
                                </Button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                {passTypes.map((val) => (
                    <div key={val.id}>
                        <table className="pass-comparison-little-table">
                            <caption className="rec-sr-only">Pass types</caption>
                            <thead>
                                <tr>
                                    <th>{val.type}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map(
                                    (pass) =>
                                        val.values.indexOf(pass.value) !== -1 && (
                                            <tr key={pass.id}>
                                                <td>{pass.title}</td>
                                            </tr>
                                        )
                                )}
                            </tbody>
                        </table>
                        <br />
                    </div>
                ))}
            </ContentBlock>
        );
    }
}

PassComparisonSection.propTypes = propTypes;
PassComparisonSection.defaultProps = defaultProps;
